import type { FC } from 'react'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { ShevronLeftIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronLeftIcon'
import type { FundingRequestData } from '@/api/kubik/monolith'
import { createConsultingRequestBody } from '@/components/composed/ConsultingForm/utils'
import { ConsultingFormAgreement } from '@/components/composed/finansingConsulting/ConsultingFormAgreement'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { TextInput } from '@/components/ui/ReactHookFormFields'
import { getUser } from '@/logic/auth/reducer'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { LOAN_PRODUCTS_HAS_ONLY_CONSULT_FORM } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/components/LoanProductsList/components/LoanProduct/components/LoanProductCollapsing/constants'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import { normalizePhoneNumber } from '@/utils/formatUtils'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { messages } from './messages'
import type { IConsultingFormValues, ILoanProductConsultFormProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  user: getUser()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

export const LoanProductConsultForm: FC<ILoanProductConsultFormProps> = props => {
  const { onBack, product, onCreateConsultRequest, isLoading, customComment } = props

  const { user, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()

  const { setOptions, profile } = useApplyingFinancingOfferForm({
    productId: FundingProduct.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
  })

  const initialValues: IConsultingFormValues = {
    loanAmount: '',
    purpose: '',
    phone: profile.phone,
    agreement: false,
  }

  const { ...formMethods } = useForm<IConsultingFormValues>({ defaultValues: initialValues })
  const { control, handleSubmit, setValue } = formMethods
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('phone', normalizePhoneNumber(profile.phone)), [profile.phone])

  const onSubmit = handleSubmit(async (values: IConsultingFormValues) => {
    if (isAuthenticated) {
      const body = createConsultingRequestBody({ values, user, productId: product.productId as FundingProduct })

      if (isApplyingOfferRequestBody(body)) {
        const productIds = [FundingProduct.MARKETPLACE]

        const response = await onCreateConsultRequest({
          agreement: body.agreement,
          comment: customComment || body.comment,
          email: body.email,
          loanAmount: body.loanAmount,
          phone: body.phone,
          productIds,
        })

        pushGtmEventOnSubmitOrder(
          userGtmInfo,
          userRegion.name,
          response.data.fundingRequests as Pick<FundingRequestData, 'id' | 'multiId'>[],
          productIds,
          EGtmBlock.CONSULTATION,
        )
      }
    } else {
      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: normalizePhoneNumber(values.phone) },
        onCloseDialog: () =>
          setOptions({ formValues: createConsultingRequestBody({ values, user, productId: product.productId as FundingProduct }) }),
      })
    }
  })

  return (
    <div className="loanProductConsultForm">
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <div className="loanProductConsultFormContent">
            <h3 className="loanProductConsultFormContent__title">{messages.title}</h3>
            <p>{messages.description}</p>

            <div className="loanProductConsultFormFields">
              <TextInput
                name="phone"
                type="tel"
                placeholder={messages.form.phone.placeholder}
                rules={{ required: messages.form.errors.required }}
                control={control}
                shouldBeTouchedBeforeError={false}
                isRequired
              />
            </div>

            <ConsultingFormAgreement />
          </div>

          <div className="loanProductConsultFormActions">
            {onBack && (
              <Button
                isHidden={LOAN_PRODUCTS_HAS_ONLY_CONSULT_FORM.includes(product.productId as FundingProduct)}
                onClick={onBack}
                modifiers={['outline-gray']}
                iconPosition="left"
                iconProps={{ component: <ShevronLeftIcon /> }}
              >
                {messages.actions.back}
              </Button>
            )}

            <Button type="submit" modifiers={['green']} loaderProps={{ isVisible: isLoading }}>
              {messages.actions.submit}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
